<template>
  <BaseModal :modalTitle="$t('W: Seed ordrer')">
    <template #titleBadge>
      <DevBadge />
    </template>
    <UiBaseFormVee
      :errorText="errorText"
      :onSubmit="onSubmit">
      <div class="mb-3">
        <p>{{ $t('W: Kun for kontoen:') }}</p>
        <p class="font-semibold">{{ labAccount?.name ?? '' }}</p>
        <p>
          {{
            $t(
              'W: Opretter {count} DEV-ordrer, alle tilknyttet den valgte kunde og bruger.',
              { count: SEED_ORDER_COUNT }
            )
          }}
        </p>
      </div>

      <UiComboboxVee
        name="customerAccountID"
        :label="$t('W: Kunde')"
        :rules="['required']"
        isRequired
        v-model="form.customerAccountID"
        :options="customerAccountOptions" />

      <UiComboboxVee
        name="customerUserID"
        :label="$t('W: Bruger')"
        :rules="['required']"
        isRequired
        :isDisabled="!form.customerAccountID"
        v-model="form.customerUserID"
        :options="customerUserOptions" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.cancel')"
          theme="neutral"
          :isDisabled="isLoading"
          @button-click="onCancelClick" />
        <UiBaseButton
          :text="$t('action.continue')"
          theme="primary"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useAccountStore } from '@/stores/accountStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useDevSeedStore } from '@/stores/devSeedStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { SEED_ORDER_COUNT } from '@/dev/seed/runSeedOrders'

export default {
  name: 'SeedOrdersModal',
  emits: ['close'],
  data() {
    return {
      SEED_ORDER_COUNT,
      form: {
        customerAccountID: '',
        customerUserID: '',
      },
      isLoading: false,
      errorText: '',
    }
  },
  computed: {
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useCustomerAccountStore, [
      'allCustomerAccounts',
      'customerAccountDisplayNamesByID',
    ]),
    ...mapState(useCustomerUserStore, ['customerUsersByAccountID']),
    customerAccountOptions(): { value: string; text: string }[] {
      return this.allCustomerAccounts
        .filter((x) => x.activeState === 'active')
        .map((customerAccount) => ({
          value: customerAccount.id,
          text:
            this.customerAccountDisplayNamesByID?.[customerAccount.id] ??
            customerAccount.name,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    customerUserOptions(): { value: string; text: string }[] {
      const users =
        this.customerUsersByAccountID[this.form.customerAccountID] ?? []
      return users
        .filter((x) => x.activeState === 'active')
        .map((user) => ({
          value: user.id,
          text: user.fullName,
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
  },
  watch: {
    'form.customerAccountID'() {
      this.form.customerUserID = this.customerUserOptions[0]?.value ?? ''
    },
  },
  methods: {
    ...mapActions(useDevSeedStore, ['seedOrders']),
    async onSubmit(): Promise<void> {
      const { success, error } = useToast()
      this.isLoading = true
      this.errorText = ''
      try {
        await this.seedOrders(
          this.form.customerAccountID,
          this.form.customerUserID
        )
        success(this.$t('W: Ordrer seedet'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('W: Der opstod en fejl')
        error(this.$t('W: Noget gik galt under seeding af ordrer'))
      } finally {
        this.isLoading = false
      }
    },
    onCancelClick(): void {
      this.$emit('close')
    },
  },
  created() {
    this.form.customerAccountID = this.customerAccountOptions[0]?.value ?? ''
    this.form.customerUserID = this.customerUserOptions[0]?.value ?? ''
  },
}
</script>
